/**
 * F.6.2 · Client API per ContractModification.
 *
 * F.6.2C · Dopo il consolidamento delle API pubbliche esiste UN SOLO endpoint
 * di scrittura per Contract Modifications:
 *   · Quantity Variation → applyQuantityVariation() (POST /quantity-variations)
 *   · New Work           → gestito dal flusso Offerta (contractualize)
 *
 * Le funzioni legacy `createQuantityVariation`, `updateModification`,
 * `transitionModification`, `applyModification` sono state RIMOSSE in F.6.2C.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

// --------- Read ---------
export const listModifications = (subcontractId, params = {}) =>
  axios.get(`${API}/api/subcontracts/${subcontractId}/modifications`, { ...auth(), params })
    .then(r => r.data.modifications || []);

export const getModification = (id) =>
  axios.get(`${API}/api/contract-modifications/${id}`, auth()).then(r => r.data);

export const listRevisionSnapshots = (subcontractId) =>
  axios.get(`${API}/api/subcontracts/${subcontractId}/revision-snapshots`, auth())
    .then(r => r.data.snapshots || []);

// --------- Write (F.6.2A · endpoint unificato) ---------
/**
 * F.6.2A · Endpoint unificato Quantity Variation.
 * Il backend orchestra create → emit → apply → snapshot → read model.
 * Il frontend fa UNA SOLA chiamata HTTP.
 *
 * Ritorna: { modification, subcontract_id, new_contract_revision, total_amount_current, total_amount_delta }
 */
export const applyQuantityVariation = (subcontractId, lineVariations, reason, notes) =>
  axios.post(
    `${API}/api/subcontracts/${subcontractId}/quantity-variations`,
    { line_variations: lineVariations, reason, notes: notes || null },
    auth(),
  ).then(r => r.data);

// --------- F.6.4 · PDF download (endpoint F.6.3) ---------
/**
 * Scarica il PDF dell'appendice contrattuale (Contract Modification EFFECTIVE).
 * Mantiene il filename fornito dal backend via Content-Disposition. Se assente,
 * usa `fallbackName` (di norma il modification_number).
 */
export const downloadModificationPdf = async (modificationId, fallbackName) => {
  const resp = await axios.get(
    `${API}/api/contract-modifications/${modificationId}/pdf`,
    { ...auth(), responseType: 'blob' },
  );
  // Estrai filename da Content-Disposition (RFC 6266 semplificato).
  const cd = resp.headers?.['content-disposition'] || '';
  let filename = fallbackName ? `${fallbackName}.pdf` : `appendice-${modificationId}.pdf`;
  const m = /filename\s*=\s*"?([^"]+)"?/i.exec(cd);
  if (m && m[1]) filename = m[1];

  const url = window.URL.createObjectURL(resp.data);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Ritarda la revoke per lasciare al browser il tempo di iniziare il download.
    setTimeout(() => window.URL.revokeObjectURL(url), 4000);
  }
  return filename;
};

/**
 * URL dell'anteprima HTML della Contract Modification (endpoint F.6.3).
 * Usato SOLO in ambiente dev o per utenti admin (vedi UI: gating a livello di componente).
 * Il token va appeso come query param `token` se il backend lo accetta;
 * in alternativa lasciamo l'apertura in una nuova tab e il browser inoltrerà il cookie httpOnly.
 */
export const previewModificationHtmlUrl = (modificationId) =>
  `${API}/api/contract-modifications/${modificationId}/preview.html`;

// --------- Constants ---------
export const MODIFICATION_KIND_LABELS = {
  quantity_variation: 'Variazione quantità',
  new_work: 'Nuova lavorazione',
  price_variation: 'Variazione prezzo',
  condition_variation: 'Variazione condizioni',
};

export const MODIFICATION_STATUS_LABELS = {
  draft: 'Bozza',
  emitted: 'Emessa',
  in_review: 'In esame',
  accepted: 'Accettata',
  rejected: 'Rifiutata',
  effective: 'Efficace',
  cancelled: 'Annullata',
  superseded: 'Superata',
};

export const MODIFICATION_STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  emitted: 'bg-sky-100 text-sky-700 border-sky-300',
  in_review: 'bg-blue-100 text-blue-700 border-blue-300',
  accepted: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  rejected: 'bg-rose-100 text-rose-700 border-rose-300',
  effective: 'bg-teal-100 text-teal-800 border-teal-400',
  cancelled: 'bg-zinc-200 text-zinc-800 border-zinc-400',
  superseded: 'bg-purple-100 text-purple-700 border-purple-300',
};

export const fmtEUR = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n));
};

export const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
};
